import { useEffect, useState, useMemo } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Modal,
  Row,
  Col,
  Spinner,
  Alert,
  Image,
  Tabs,
  Tab,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import "../css/admin.css";
import { API_BASE_URL } from "../config";

const WORKOUTS_API = `${API_BASE_URL}/api/workouts`;
const USERS_API = `${API_BASE_URL}/api/admin/users`;

const EMPTY = {
  id: null,
  slug: "",
  title: "",
  subtitle: "",
  level: "beginner",
  weeks: 4,
  sessionsPerWeek: 3,
  status: "available", // available | coming_soon
  price: 0,
  currency: "EUR",
  stripePriceId: "",
  coverUrl: "/uploads/covers/placeholder.jpg",
  pdfPath: "",
  shortDescription: "",
};

export default function Admin() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Solo admin
  useEffect(() => {
    const role = (user?.role || user?.ruolo || "").trim().toLowerCase();
    if (role !== "admin") navigate("/");
  }, [user, navigate]);

  const [tab, setTab] = useState("workouts");

  // ===== Workouts =====
  const [editCoverUrl, setEditCoverUrl] = useState(false);
  const [coachingModal, setCoachingModal] = useState(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [contactModal, setContactModal] = useState(null);

  // Filtri tabella workouts
  const [q, setQ] = useState("");
  const [lvl, setLvl] = useState("all");
  const [stat, setStat] = useState("all");

  // ===== Coaching (admin) =====
  const [coaching, setCoaching] = useState([]);
  const [loadingCoaching, setLoadingCoaching] = useState(false);

  // ===== Users =====
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errUsers, setErrUsers] = useState("");

  // ===== Payment link modal (nuovo) =====

  // ===== Nuovi stati per Contacts =====
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [errContacts, setErrContacts] = useState("");
  const [contactView, setContactView] = useState(null);

  // ===== Fetch: contacts (admin) =====
  const fetchContacts = () => {
    setLoadingContacts(true);
    setErrContacts("");
    fetch(`${API_BASE_URL}/api/admin/contacts`, {
      method: "GET",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setContacts(Array.isArray(data) ? data : []))
      .catch((e) => {
        console.error(e);
        setErrContacts("Impossibile caricare le richieste di contatto.");
      })
      .finally(() => setLoadingContacts(false));
  };

  // === Fetch: workouts (pubblici; l’admin usa gli stessi in lettura)
  const fetchWorkouts = () => {
    setLoading(true);
    setErrMsg("");
    fetch(WORKOUTS_API, {
      method: "GET",
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch((e) => {
        console.error(e);
        setErrMsg("Impossibile caricare i workout.");
      })
      .finally(() => setLoading(false));
  };

  // === Fetch: coaching (admin)
  const fetchCoaching = () => {
    setLoadingCoaching(true);
    fetch(`${API_BASE_URL}/api/admin/coaching-subscriptions`, {
      method: "GET",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setCoaching(Array.isArray(data) ? data : []))
      .catch((e) => console.error(e))
      .finally(() => setLoadingCoaching(false));
  };

  // === Fetch: users (admin)
  const fetchUsers = () => {
    setLoadingUsers(true);
    setErrUsers("");
    fetch(USERS_API, {
      method: "GET",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch((e) => {
        console.error(e);
        setErrUsers("Impossibile caricare gli utenti.");
      })
      .finally(() => setLoadingUsers(false));
  };
  // === Delete coaching subscription (admin)
  const deleteCoaching = async (id) => {
    if (!id) return;
    if (!window.confirm("Eliminare questa richiesta di coaching?")) return;
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/admin/coaching-subscriptions/${id}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      if (!r.ok) throw new Error(await r.text());
      fetchCoaching();
    } catch (e) {
      console.error(e);
      alert("Errore durante l'eliminazione della richiesta coaching.");
    }
  };

  // === Delete contact request (admin)
  const deleteContact = async (id) => {
    if (!id) return;
    if (!window.confirm("Eliminare questa richiesta di contatto?")) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/contacts/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!r.ok) throw new Error(await r.text());
      fetchContacts();
    } catch (e) {
      console.error(e);
      alert("Errore durante l'eliminazione della richiesta contatto.");
    }
  };

  // === Delete user (admin)
  const deleteUser = async (id) => {
    if (!id) return;
    if (!window.confirm("Eliminare definitivamente questo utente?")) return;
    setErrUsers("");
    try {
      const r = await fetch(`${USERS_API}/${id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: "include",
      });
      if (!r.ok) throw new Error(await r.text());
      fetchUsers();
    } catch (e) {
      console.error(e);
      setErrUsers("Errore durante l'eliminazione utente.");
    }
  };

  // Prime fetch
  useEffect(() => {
    fetchWorkouts();
    fetchCoaching();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ===== Modale workout (create/edit) =====
  const openModal = (item = null) => {
    setEditCoverUrl(false);

    if (item) {
      setForm({
        ...EMPTY,
        ...item,
        coverUrl:
          item.coverUrl && item.coverUrl.trim()
            ? item.coverUrl
            : EMPTY.coverUrl,
      });
    } else {
      setForm({ ...EMPTY });
    }
    setShow(true);
  };

  const closeModal = () => {
    if (!saving) setShow(false);
  };

  // ===== Input handler workout form =====
  const onChange = (e) => {
    const { name, value } = e.target;

    if (["weeks", "sessionsPerWeek"].includes(name)) {
      return setForm((s) => ({
        ...s,
        [name]: value === "" ? "" : Number(value),
      }));
    }
    if (name === "price") {
      return setForm((s) => ({
        ...s,
        price: value === "" ? "" : Number(value),
      }));
    }
    if (name === "level") {
      return setForm((s) => ({ ...s, level: value.toLowerCase() }));
    }
    if (name === "status") {
      return setForm((s) => ({ ...s, status: value.toLowerCase() }));
    }
    if (name === "currency") {
      return setForm((s) => ({ ...s, currency: value.toUpperCase() }));
    }

    setForm((s) => ({ ...s, [name]: value }));
  };

  // ===== Upload helper (cover pubblica / PDF privato) =====
  const uploadFile = async (file, kind = "cover") => {
    const url =
      kind === "cover"
        ? `${API_BASE_URL}/api/admin/uploads/cover`
        : `${API_BASE_URL}/api/admin/uploads/pdf`;

    const fd = new FormData();
    fd.append("file", file);

    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    const res = await fetch(url, {
      method: "POST",
      headers,
      body: fd,
      credentials: "include",
    });
    if (res.status === 403)
      throw new Error("403 — Only admins can upload files.");
    if (!res.ok) throw new Error(await res.text());
    return res.json(); // cover → {url}, pdf → {path}
  };

  // ===== Validazione workout =====
  const valid = useMemo(() => {
    const f = form;
    if (!f.slug.trim()) return false;
    if (!f.title.trim()) return false;
    if (!f.level) return false;
    if (!f.status) return false;
    if (!(Number(f.weeks) > 0)) return false;
    if (!(Number(f.sessionsPerWeek) > 0)) return false;

    if (f.status === "available") {
      if (!(Number(f.price) > 0)) return false;
      if (!f.currency?.trim()) return false;
      if (!f.stripePriceId?.trim()) return false;
    }
    return true;
  }, [form]);

  // ===== Submit workout (create/update) =====
  const onSubmit = (e) => {
    e.preventDefault();
    if (!valid) return;

    setSaving(true);
    setErrMsg("");

    // payload normalizzato
    const payload = {
      ...form,
      slug: form.slug.trim(),
      title: form.title.trim(),
      subtitle: form.subtitle?.trim() || "",
      level: (form.level || "").toLowerCase().trim(),
      status: (form.status || "").toLowerCase().trim(),
      weeks: Number(form.weeks),
      sessionsPerWeek: Number(form.sessionsPerWeek),
      price: form.status === "available" ? Number(form.price) : 0,
      currency: (form.currency || "EUR").toUpperCase().trim(),
      stripePriceId:
        form.status === "available" ? (form.stripePriceId || "").trim() : "",
      coverUrl:
        form.coverUrl && form.coverUrl.trim()
          ? form.coverUrl.trim()
          : EMPTY.coverUrl,

      pdfPath: form.pdfPath?.trim() || "",
      shortDescription: form.shortDescription?.trim() || "",
    };

    const isUpdate = !!payload.id;
    const url = isUpdate ? `${WORKOUTS_API}/${payload.id}` : WORKOUTS_API;
    const method = isUpdate ? "PUT" : "POST";

    fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
      })
      .then(() => {
        closeModal();
        fetchWorkouts();
      })
      .catch((e) => {
        console.error(e);
        setErrMsg(
          "Errore durante il salvataggio. Controlla i campi e riprova."
        );
      })
      .finally(() => setSaving(false));
  };

  // ===== Delete workout =====
  const onDeleteWorkout = (id) => {
    if (!id) return;
    if (!window.confirm("Eliminare definitivamente questo workout?")) return;
    setErrMsg("");

    fetch(`${WORKOUTS_API}/${id}`, {
      method: "DELETE",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        fetchWorkouts();
      })
      .catch((e) => {
        console.error(e);
        setErrMsg("Errore durante l'eliminazione.");
      });
  };

  // ===== Filtri dinamici =====
  const filtered = useMemo(() => {
    let list = [...items];
    const qq = q.trim().toLowerCase();

    if (qq) {
      list = list.filter(
        (i) =>
          (i.title || "").toLowerCase().includes(qq) ||
          (i.subtitle || "").toLowerCase().includes(qq) ||
          (i.slug || "").toLowerCase().includes(qq)
      );
    }
    if (lvl !== "all")
      list = list.filter((i) => (i.level || "").toLowerCase() === lvl);
    if (stat !== "all")
      list = list.filter((i) => (i.status || "").toLowerCase() === stat);

    return list;
  }, [items, q, lvl, stat]);

  const levels = useMemo(() => {
    const set = new Set(
      items.map((i) => (i.level || "").toLowerCase()).filter(Boolean)
    );
    return ["all", ...Array.from(set).sort()];
  }, [items]);

  const statuses = useMemo(() => {
    const set = new Set(
      items.map((i) => (i.status || "").toLowerCase()).filter(Boolean)
    );
    const arr = Array.from(set);
    ["available", "coming_soon"].forEach((v) => {
      if (!arr.includes(v)) arr.push(v);
    });
    return ["all", ...arr.sort()];
  }, [items]);

  // ===== Render =====
  return (
    <div className="admin-page">
      <Container className="py-4">
        <h2 className="admin-title">Admin</h2>
        <Tabs
          activeKey={tab}
          onSelect={(k) => {
            const key = k || "workouts";
            setTab(key);
            if (key === "users") fetchUsers();
            else if (key === "coaching") fetchCoaching();
            else if (key === "workouts") fetchWorkouts();
            else if (key === "contacts") fetchContacts();
          }}
        >
          {/* ====================== TAB WORKOUTS ====================== */}
          <Tab eventKey="workouts" title="Workouts">
            {/* Toolbar */}
            <Row className="g-3 align-items-end mb-3">
              <Col xs={12} sm={6} md={4}>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  placeholder="title / subtitle / slug…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </Col>
              <Col xs={6} sm={3} md={2}>
                <Form.Label>Level</Form.Label>
                <Form.Select
                  value={lvl}
                  onChange={(e) => setLvl(e.target.value)}
                >
                  {levels.map((lv) => (
                    <option key={lv} value={lv}>
                      {lv === "all" ? "All" : lv[0].toUpperCase() + lv.slice(1)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} sm={3} md={2}>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={stat}
                  onChange={(e) => setStat(e.target.value)}
                >
                  {statuses.map((st) => (
                    <option key={st} value={st}>
                      {st === "all" ? "All" : st.replace("_", " ")}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md="auto">
                <Button variant="warning" onClick={() => openModal()}>
                  ➕ Add Workout
                </Button>
              </Col>
            </Row>

            {errMsg && <Alert variant="danger">{errMsg}</Alert>}

            {/* Tabella Workouts */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : (
              <Table striped bordered hover className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Slug</th>
                    <th>Title</th>
                    <th>Level</th>
                    <th>Weeks</th>
                    <th>Sessions/wk</th>
                    <th>Status</th>
                    <th>Price</th>
                    <th>Stripe Price</th>
                    <th>Cover</th>
                    <th>PDF</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((w) => (
                    <tr key={w.id}>
                      <td>{w.id}</td>
                      <td>{w.slug}</td>
                      <td>{w.title}</td>
                      <td>{w.level}</td>
                      <td>{w.weeks}</td>
                      <td>{w.sessionsPerWeek}</td>
                      <td>{w.status || "—"}</td>
                      <td>
                        {typeof w.price === "number" && w.price > 0
                          ? `${w.price} ${w.currency || "EUR"}`
                          : "—"}
                      </td>
                      <td className="text-truncate" style={{ maxWidth: 160 }}>
                        {w.stripePriceId || "—"}
                      </td>
                      <td>
                        {w.coverUrl ? (
                          <Image
                            src={w.coverUrl}
                            alt={w.title}
                            width={60}
                            rounded
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{w.hasPdf ? "✅" : "—"}</td>
                      <td className="admin-actions">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => openModal(w)}
                        >
                          Modifica
                        </Button>{" "}
                        <Button
                          size="sm"
                          variant="danger"
                          className="mt-1"
                          onClick={() => onDeleteWorkout(w.id)}
                        >
                          Elimina
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Tab>
          {/* ====================== TAB COACHING ====================== */}
          <Tab eventKey="coaching" title="Coaching">
            <h3 className="mb-3">Coaching Subscriptions</h3>

            {loadingCoaching ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Package</th>
                    <th>Status</th>
                    <th>Timeline</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {coaching.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted">
                        Nessuna richiesta di coaching.
                      </td>
                    </tr>
                  ) : (
                    coaching.map((c) => (
                      <tr key={c.id}>
                        <td>{c.id}</td>
                        <td>{c.userId ?? "-"}</td>
                        <td>{c.packageTitle || c.packageId || "-"}</td>
                        <td>{c.status || "-"}</td>
                        <td>{c.timeline || "-"}</td>
                        <td>
                          {c.createdAt
                            ? String(c.createdAt).slice(0, 19).replace("T", " ")
                            : "-"}
                        </td>

                        <td className="admin-actions">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setCoachingModal(c)}
                          >
                            View
                          </Button>{" "}
                          <Button
                            size="sm"
                            variant="success"
                            disabled={
                              (c.status || "").toLowerCase() === "active"
                            }
                            onClick={async () => {
                              try {
                                const r = await fetch(
                                  `${API_BASE_URL}/api/admin/coaching-subscriptions/${c.id}/activate`,
                                  {
                                    method: "PATCH",
                                    credentials: "include",
                                    headers: token
                                      ? { Authorization: `Bearer ${token}` }
                                      : undefined,
                                  }
                                );
                                if (!r.ok) throw new Error(await r.text());
                                fetchCoaching();
                              } catch (e) {
                                console.error(e);
                                alert(
                                  "Errore durante l'attivazione del coaching."
                                );
                              }
                            }}
                          >
                            Activate
                          </Button>{" "}
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => deleteCoaching(c.id)}
                          >
                            Elimina
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}

            {/* ============ MODAL COACHING DETAILS ============ */}
            <Modal
              show={!!coachingModal}
              onHide={() => setCoachingModal(null)}
              centered
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title>Coaching details</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                {coachingModal ? (
                  <>
                    {/* meta */}
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="small text-muted">ID</div>
                        <div className="fw-semibold">{coachingModal.id}</div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">User ID</div>
                        <div className="fw-semibold">
                          {coachingModal.userId ?? "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Status</div>
                        <div className="fw-semibold">
                          {coachingModal.status || "-"}
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="small text-muted">Package</div>
                        <div className="fw-semibold">
                          {coachingModal.packageTitle ||
                            coachingModal.packageId ||
                            "-"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="small text-muted">Timeline</div>
                        <div className="fw-semibold">
                          {coachingModal.timeline || "-"}
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="small text-muted">Created</div>
                        <div className="fw-semibold">
                          {coachingModal.createdAt
                            ? String(coachingModal.createdAt)
                                .slice(0, 19)
                                .replace("T", " ")
                            : "-"}
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="small text-muted">Start</div>
                        <div className="fw-semibold">
                          {coachingModal.startDate || "-"}
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="small text-muted">End</div>
                        <div className="fw-semibold">
                          {coachingModal.endDate || "-"}
                        </div>
                      </Col>
                    </Row>

                    <hr className="my-4" />

                    {/* form fields */}
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="small text-muted">Name</div>
                        <div className="fw-semibold">
                          {coachingModal.name || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Email</div>
                        <div className="fw-semibold">
                          {coachingModal.email || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Phone</div>
                        <div className="fw-semibold">
                          {coachingModal.phone || "-"}
                        </div>
                      </Col>

                      <Col md={3}>
                        <div className="small text-muted">Sex</div>
                        <div className="fw-semibold">
                          {coachingModal.sex || "-"}
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="small text-muted">Age</div>
                        <div className="fw-semibold">
                          {coachingModal.age != null ? coachingModal.age : "-"}
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="small text-muted">Height</div>
                        <div className="fw-semibold">
                          {coachingModal.height != null
                            ? coachingModal.height
                            : "-"}
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="small text-muted">Weight</div>
                        <div className="fw-semibold">
                          {coachingModal.weight != null
                            ? coachingModal.weight
                            : "-"}
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="small text-muted">Experience</div>
                        <div className="fw-semibold">
                          {coachingModal.experience || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Objective</div>
                        <div className="fw-semibold">
                          {coachingModal.objective || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Frequency</div>
                        <div className="fw-semibold">
                          {coachingModal.frequency || "-"}
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="small text-muted">
                          Preferred coaching
                        </div>
                        <div className="fw-semibold">
                          {coachingModal.coaching || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Timezone</div>
                        <div className="fw-semibold">
                          {coachingModal.timezone || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Call times</div>
                        <div className="fw-semibold">
                          {coachingModal.calltimes || "-"}
                        </div>
                      </Col>

                      <Col md={12}>
                        <div className="small text-muted">Lead source</div>
                        <div className="fw-semibold">
                          {coachingModal.leadSource ||
                            coachingModal.lead_source ||
                            "-"}
                        </div>
                      </Col>

                      <Col md={12}>
                        <div className="small text-muted">Message</div>
                        <div
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {coachingModal.message || "-"}
                        </div>
                      </Col>
                    </Row>
                  </>
                ) : null}
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setCoachingModal(null)}
                >
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </Tab>
          {/* ====================== TAB CONTACTS ====================== */}
          <Tab eventKey="contacts" title="Contacts">
            <h3 className="mb-3">Contact Requests</h3>

            {loadingContacts ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : errContacts ? (
              <Alert variant="danger">{errContacts}</Alert>
            ) : (
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {contacts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        Nessuna richiesta di contatto.
                      </td>
                    </tr>
                  ) : (
                    contacts.map((c) => (
                      <tr key={c.id}>
                        <td>{c.id}</td>
                        <td>{c.name || "-"}</td>
                        <td>{c.email || "-"}</td>
                        <td>{c.phone || "-"}</td>
                        <td>
                          {c.createdAt
                            ? String(c.createdAt).slice(0, 19).replace("T", " ")
                            : "-"}
                        </td>
                        <td className="admin-actions">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setContactView(c)}
                          >
                            View
                          </Button>{" "}
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => deleteContact(c.id)}
                          >
                            Elimina
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}

            {/* ============ MODAL CONTACT DETAILS ============ */}
            <Modal
              show={!!contactView}
              onHide={() => setContactView(null)}
              centered
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title>Contact details</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                {contactView ? (
                  <>
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="small text-muted">ID</div>
                        <div className="fw-semibold">{contactView.id}</div>
                      </Col>
                      <Col md={8}>
                        <div className="small text-muted">Created</div>
                        <div className="fw-semibold">
                          {contactView.createdAt
                            ? String(contactView.createdAt)
                                .slice(0, 19)
                                .replace("T", " ")
                            : "-"}
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="small text-muted">Name</div>
                        <div className="fw-semibold">
                          {contactView.name || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Email</div>
                        <div className="fw-semibold">
                          {contactView.email || "-"}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Phone</div>
                        <div className="fw-semibold">
                          {contactView.phone || "-"}
                        </div>
                      </Col>

                      <Col md={12}>
                        <div className="small text-muted">Message</div>
                        <div
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {contactView.message || "-"}
                        </div>
                      </Col>
                    </Row>

                    <hr className="my-4" />

                    <Row className="g-3">
                      <Col md={4}>
                        <div className="small text-muted">IP</div>
                        <div className="fw-semibold">
                          {contactView.ip || "-"}
                        </div>
                      </Col>
                      <Col md={8}>
                        <div className="small text-muted">User Agent</div>
                        <div style={{ wordBreak: "break-word" }}>
                          {contactView.userAgent || "-"}
                        </div>
                      </Col>
                    </Row>
                  </>
                ) : null}
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setContactView(null)}
                >
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </Tab>

          {/* ====================== TAB USERS ====================== */}
          <Tab eventKey="users" title="Users">
            {errUsers && <Alert variant="danger">{errUsers}</Alert>}
            {loadingUsers ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : (
              <Table striped bordered hover className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Created</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        Nessun utente trovato.
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>{u.email}</td>
                        <td>{u.name || u.fullName || "-"}</td>
                        <td>{(u.role || u.ruolo || "-").toString()}</td>
                        <td>
                          {u.createdAt
                            ? String(u.createdAt).slice(0, 19).replace("T", " ")
                            : "-"}
                        </td>
                        <td className="admin-actions">
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => deleteUser(u.id)}
                          >
                            Elimina
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Tab>
        </Tabs>

        {/* ====================== MODAL CREATE/EDIT WORKOUT ====================== */}
        <Modal show={show} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {form.id ? "Modifica Workout" : "Aggiungi Workout"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={onSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Slug*</Form.Label>
                  <Form.Control
                    name="slug"
                    value={form.slug}
                    onChange={onChange}
                    placeholder="es. full-body-beginner-8w"
                    required
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Level*</Form.Label>
                  <Form.Select
                    name="level"
                    value={form.level}
                    onChange={onChange}
                  >
                    {[
                      "beginner",
                      "intermediate",
                      "novice",
                      "advanced",
                      "athlete",
                    ].map((lv) => (
                      <option key={lv} value={lv}>
                        {lv[0].toUpperCase() + lv.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={8}>
                  <Form.Label>Title*</Form.Label>
                  <Form.Control
                    name="title"
                    value={form.title}
                    onChange={onChange}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Status*</Form.Label>
                  <Form.Select
                    name="status"
                    value={form.status}
                    onChange={onChange}
                  >
                    {["available", "coming_soon"].map((s) => (
                      <option key={s} value={s}>
                        {s.replace("_", " ")}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={12}>
                  <Form.Label>Subtitle</Form.Label>
                  <Form.Control
                    name="subtitle"
                    value={form.subtitle}
                    onChange={onChange}
                  />
                </Col>

                <Col md={4}>
                  <Form.Label>Weeks*</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    name="weeks"
                    value={form.weeks}
                    onChange={onChange}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Sessions/week*</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    name="sessionsPerWeek"
                    value={form.sessionsPerWeek}
                    onChange={onChange}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Currency</Form.Label>
                  <Form.Select
                    name="currency"
                    value={form.currency}
                    onChange={onChange}
                  >
                    {["EUR", "USD", "GBP"].map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Label>
                    Price{form.status === "available" ? " *" : ""}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    name="price"
                    value={form.price}
                    onChange={onChange}
                    required={form.status === "available"}
                    disabled={form.status !== "available"}
                  />
                </Col>
                <Col md={8}>
                  <Form.Label>
                    Stripe Price ID{form.status === "available" ? " *" : ""}
                  </Form.Label>
                  <Form.Control
                    name="stripePriceId"
                    value={form.stripePriceId}
                    onChange={onChange}
                    placeholder="es. price_12345"
                    required={form.status === "available"}
                    disabled={form.status !== "available"}
                  />
                </Col>

                {/* Cover URL (di default bloccato) */}
                <Col md={8}>
                  <Form.Label className="d-flex align-items-center justify-content-between">
                    <span>Cover URL</span>

                    <Button
                      type="button"
                      size="sm"
                      variant={
                        editCoverUrl ? "outline-danger" : "outline-secondary"
                      }
                      onClick={() => setEditCoverUrl((v) => !v)}
                    >
                      {editCoverUrl ? "Lock" : "Edit manually"}
                    </Button>
                  </Form.Label>

                  <Form.Control
                    name="coverUrl"
                    value={form.coverUrl}
                    onChange={onChange}
                    placeholder="/uploads/covers/placeholder.jpg"
                    readOnly={!editCoverUrl}
                  />

                  {!editCoverUrl && (
                    <div className="small text-muted mt-1">
                      Tip: usa <strong>Upload Cover</strong>. Questo campo si
                      compila da solo.
                    </div>
                  )}
                </Col>

                <Col md={4} className="d-flex align-items-end">
                  {form.coverUrl ? (
                    <Image
                      src={form.coverUrl}
                      alt="preview"
                      width={80}
                      rounded
                    />
                  ) : null}
                </Col>

                {/* Upload cover (pubblica) */}
                <Col md={8}>
                  <Form.Label>Upload Cover</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try {
                        const out = await uploadFile(f, "cover"); // { url }
                        setForm((s) => ({
                          ...s,
                          coverUrl: out.url || s.coverUrl,
                        }));
                      } catch (err) {
                        alert("Upload cover fallito");
                        console.error(err);
                      }
                    }}
                  />
                </Col>

                {/* Upload PDF (privato) */}
                <Col md={8}>
                  <Form.Label>Upload PDF (privato)</Form.Label>
                  <Form.Control
                    type="file"
                    accept="application/pdf"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try {
                        const out = await uploadFile(f, "pdf"); // { path }
                        setForm((s) => ({
                          ...s,
                          pdfPath: out.path || s.pdfPath,
                        }));
                      } catch (err) {
                        alert("Upload PDF fallito");
                        console.error(err);
                      }
                    }}
                  />
                  {form.pdfPath ? (
                    <div className="small text-success mt-1">
                      PDF caricato: <code>{form.pdfPath}</code>
                    </div>
                  ) : null}
                </Col>

                <Col md={12}>
                  <Form.Label>Short Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="shortDescription"
                    value={form.shortDescription}
                    onChange={onChange}
                    placeholder="Breve pitch che apparirà nella pagina di dettaglio."
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="secondary"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Annulla
                </Button>
                <Button
                  variant="success"
                  type="submit"
                  disabled={!valid || saving}
                >
                  {saving ? "Salvataggio…" : form.id ? "Aggiorna" : "Aggiungi"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ====================== MODAL CONTACT MESSAGE ====================== */}
        <Modal
          show={!!contactModal}
          onHide={() => setContactModal(null)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Contact request</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {contactModal ? (
              <>
                <div className="mb-2">
                  <strong>Name:</strong> {contactModal.name || "-"}
                </div>
                <div className="mb-2">
                  <strong>Email:</strong> {contactModal.email || "-"}
                </div>
                {contactModal.phone ? (
                  <div className="mb-2">
                    <strong>Phone:</strong> {contactModal.phone}
                  </div>
                ) : null}
                <div className="mb-3 text-muted small">
                  <strong>Created:</strong>{" "}
                  {contactModal.createdAt
                    ? String(contactModal.createdAt)
                        .slice(0, 19)
                        .replace("T", " ")
                    : "-"}
                </div>

                <hr />

                <div
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {contactModal.message || "-"}
                </div>
              </>
            ) : null}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setContactModal(null)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}
